import {
  Button, DatePicker, Dropdown,
  useSnackbar,
} from "@kitbird/core";
import styled from "styled-components";
import { Controller, useWatch } from "react-hook-form";
import { useQuery } from "react-query";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import Modal from "../../../../../components/Modal";
import useCreateReceiptSubmission from "../../hooks/useCreateReceiptSubmission";
import { IOptionItem } from "../../../../../typings/common";
import { getMasterCompanyRelationByCompanyId } from "../../../../../services/masterCompanyService";
import { getTrxReportDoc } from "../../../../../services/trxReportService";
import { getDocUrl } from "../../../../../services/bbOneGcsService";
import useAuth from "../../../../../components/Authentication/useAuth";
import { getBusinessCompany } from "../../../../../services/businessService/businessService";

interface PrintModalI {
  useCustomHook: ReturnType<typeof useCreateReceiptSubmission>,
}

const FormWrapper = styled.div`
  display: grid;
  grid-template-columns: calc(50% - 12px) calc(50% - 12px);
  gap: 24px;
`;

const PrintModal = ({ useCustomHook }: PrintModalI) => {
  const {
    dropdownArea,
    dropdownPool,
    isPrintModal,
    setPrintModal,
    setLoading,
    printMethods,
    initFetch,
  } = useCustomHook;
  const snackbar = useSnackbar();
  const { userAccount } = useAuth();

  const {
    control, handleSubmit, reset, setValue, watch, resetField,
  } = printMethods;
  const [companyOpts, setCompanyOpts] = useState<IOptionItem[]>([]);
  const [companyRelationOpts, setCompanyRelationOpts] = useState<IOptionItem[]>([]);

  const [codeTypeOpts, setCodeTypeOpts] = useState<Array<IOptionItem>>([]);
  const [segmentOpts, setSegmentOpts] = useState<Array<IOptionItem>>([]);

  useEffect(() => {
    const codeTypeOptions = Array.from(
      new Set(userAccount?.position_info?.map((item) => item.code_type)),
    ).map((item) => ({
      label: {
        O: "HO",
        P: "Pool",
        H: "Pangkalan",
      }[item] || "",
      value: item,
    }));
    setCodeTypeOpts(codeTypeOptions);
    if (codeTypeOptions.length === 1) setValue("codeType", codeTypeOptions[0]);

    const segmentOptions = Array.from(
      new Set(userAccount?.position_info?.map((item) => (
        item.segments_info
          ? JSON.stringify({
            label: item.segments_info.desc_segment,
            value: item.segments_info.code_segment,
          })
          : ""
      ))),
    ).filter((item) => item !== "").map((item) => JSON.parse(item));

    setSegmentOpts(segmentOptions);
    if (segmentOptions.length === 1) setValue("segment", segmentOptions[0]);
  }, [userAccount, isPrintModal]);

  const codeType = useWatch({ control, name: "codeType.value" });
  const area = useWatch({ control, name: "area.value" });
  const pool = useWatch({ control, name: "pool.value" });
  const segment = useWatch({ control, name: "segment.value" });

  const { data: dataCompanies } = useQuery({
    queryKey: [
      "get_companies",
      codeType,
      area,
      pool,
      segment,
    ],
    enabled: !!pool,
    queryFn: () => getBusinessCompany(
      codeType,
      area,
      pool,
      segment,
    ),
  });

  useEffect(() => {
    if (dataCompanies && dataCompanies.lstmstbusiness && dataCompanies.lstmstbusiness.length) {
      setCompanyOpts(dataCompanies.lstmstbusiness.map((company) => (
        { label: company.company_code, value: company.company_code }
      )));
    } else setCompanyOpts([]);
  }, [dataCompanies]);

  const { data: dataCompanyRelations } = useQuery({
    queryKey: ["get_company_relations", watch("company")],
    enabled: !!watch("company"),
    queryFn: () => getMasterCompanyRelationByCompanyId(watch("company.value") as string),
  });

  useEffect(() => {
    if (
      dataCompanyRelations
      && dataCompanyRelations.list_mtcompany
      && dataCompanyRelations.list_mtcompany.length
    ) {
      setValue("hasRelation", true);
      setCompanyRelationOpts(dataCompanyRelations.list_mtcompany.map((company) => (
        { label: company.company_friend, value: company.company_friend }
      )));
    } else {
      setValue("hasRelation", false);
      setValue("companyRelation", undefined);
      setCompanyRelationOpts([]);
    }
  }, [dataCompanyRelations]);

  useEffect(() => {
    if (!isPrintModal) reset();
    else initFetch();
  }, [isPrintModal]);

  const onSubmit = handleSubmit(async (val) => {
    setLoading(true);
    try {
      const date = new Date(val.transferDate);
      const stringDate = format(date, "yyyy-MM-dd");

      const payload = {
        start_date: stringDate,
        end_date: stringDate,
        pool_id: val.pool.value,
        company_code: val.company.value,
        department: userAccount.userDepartments[0],
        code_type: val.codeType.value,
      };
      const doc = await getTrxReportDoc(payload);
      if (doc.response_code === "Succeed") {
        const docUrl = await getDocUrl({
          file_name: doc.file_source.file_name,
          path_file: doc.file_source.folder_name,
        });
        window.open(docUrl)?.print();
      } else snackbar.show(doc.response_message || "Gagal memuat PDF", "error");
      setLoading(false);
    } catch (error) {
      snackbar.show("Gagal memuat PDF", "error");
      setLoading(false);
    }
  });

  const setValueResetCompany = (name: string, value: any, options?: Partial<{
    shouldValidate: boolean;
    shouldDirty: boolean;
    shouldTouch: boolean;
  }> | undefined) => {
    setValue(name, value, options);
    resetField("company");
    resetField("hasRelation");
    resetField("companyRelation");
  };

  return (
    <Modal
      isOpen={isPrintModal}
      setOpen={setPrintModal}
      title={"Print summary transaksi bon"}
      footer={<>
        <Button variant="text" onClick={() => setPrintModal(false)}>Batal</Button>
        <Button variant="primary" onClick={onSubmit}>Print</Button>
      </>}
      body={<FormWrapper>
        <Controller
          name="transferDate"
          control={control}
          render={({ field, fieldState: { error } }) => <DatePicker
            onChange={(date) => {
              setValue(field.name, date, { shouldValidate: true });
            }}
            onSubmit={(dates) => {
              setValue(field.name, dates && dates[0], { shouldValidate: true });
            }}
            onReset={() => setValue(field.name, undefined, { shouldValidate: true })}
            variant="default"
            label="Tanggal transfer"
            dateFormat="dd MMMM yyyy"
            error={error && { message: error.message || "" }}
            maxDate={new Date()}
          />}
        />
        <Controller
          name="codeType"
          control={control}
          render={({ field, fieldState: { error } }) => <Dropdown
            {...field}
            label="Tipe user"
            id={field.name}
            value={field?.value?.label}
            onSelected={(opt) => setValueResetCompany("codeType", opt, { shouldValidate: true })}
            options={codeTypeOpts}
            disabled={codeTypeOpts.length <= 1}
            error={error && { message: error.message || "" }}
          />}
        />
        <Controller
          name="area"
          control={control}
          render={({ field, fieldState: { error } }) => <Dropdown
            {...field}
            label="Wilayah"
            id={field.name}
            value={field?.value?.label}
            onSelected={(opt) => setValueResetCompany("area", opt, { shouldValidate: true })}
            options={dropdownArea}
            disabled={dropdownArea.length <= 1}
            error={error && { message: error.message || "" }}
          />}
        />
        <Controller
          name="pool"
          control={control}
          render={({ field, fieldState: { error } }) => <Dropdown
            {...field}
            label="Pool"
            id={field.name}
            value={field?.value?.label}
            onSelected={(opt) => setValueResetCompany("pool", opt, { shouldValidate: true })}
            options={dropdownPool}
            disabled={dropdownPool.length <= 1}
            error={error && { message: error.message || "" }}
          />}
        />
        <div style={{ gridColumnStart: 1, gridColumnEnd: 3 }} >
          <Controller
            name="segment"
            control={control}
            render={({ field, fieldState: { error } }) => <Dropdown
              {...field}
              label="Segment"
              id={field.name}
              value={field?.value?.label}
              onSelected={(opt) => setValueResetCompany("segment", opt, { shouldValidate: true })}
              options={segmentOpts}
              disabled={segmentOpts.length <= 1}
              error={error && { message: error.message || "" }}
            />}
          />
        </div>
        <Controller
          name="company"
          control={control}
          render={({ field, fieldState: { error } }) => <Dropdown
            {...field}
            label="Perusahaan"
            id={field.name}
            value={field?.value?.label || ""}
            onSelected={(opt) => setValue("company", opt, { shouldValidate: true })}
            options={companyOpts}
            error={error && { message: error.message || "" }}
          />}
        />
        <Controller
          name="companyRelation"
          control={control}
          render={({ field, fieldState: { error } }) => <Dropdown
            {...field}
            label="Perusahaan relasi"
            id={field.name}
            value={field?.value?.label || ""}
            onSelected={(opt) => setValue("companyRelation", opt, { shouldValidate: true })}
            options={companyRelationOpts}
            disabled={!companyRelationOpts.length}
            error={error && { message: error.message || "" }}
          />}
        />
      </FormWrapper>}
    />
  );
};

export default PrintModal;
